import { Link } from 'react-router-dom';
import {
  Cpu, PlugZap, SunDim, ScanFace, Database, Gamepad2, Smartphone,
  Microscope, Search, Wrench, CheckCircle2, MapPin,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import {
  ServiceHeroPro, ProofStrip, DiagnosisList, ProcessTimeline, PriceCardPro, AvisStrip, LocalCTA,
} from '../../components/services/elevated';
import { REVIEWS, RATING, REVIEW_COUNT } from '../../data/reviews';

const SLUG = '/micro-soudure-montpellier';

const faq = [
  { q: "Qu'est-ce que la micro-soudure ?", a: "C'est la réparation des composants soudés directement sur la carte mère d'un appareil, réalisée à la binoculaire. Elle permet de réparer des pannes qu'un simple changement de pièce ne corrige pas." },
  { q: "Combien de temps prend une réparation en micro-soudure ?", a: "Plus longtemps qu'une réparation standard : de quelques heures à quelques jours selon la complexité. Le diagnostic, lui, est gratuit." },
  { q: "Pourrai-je récupérer mes données ?", a: "Souvent oui : dans bien des cas, l'objectif premier est de rallumer l'appareil assez longtemps pour sauvegarder vos photos et contacts." },
  { q: "Réparez-vous le port HDMI des consoles ?", a: "Oui, la réparation des ports HDMI et d'alimentation des consoles fait partie de nos interventions de micro-soudure." },
];

const cas = [
  { icon: Cpu, title: "Ne s'allume plus", desc: "iPhone ou Samsung qui reste éteint ou redémarre en boucle." },
  { icon: PlugZap, title: "Charge HS persistante", desc: "Malgré un connecteur neuf (circuit de charge / IC)." },
  { icon: SunDim, title: "Plus d'image", desc: "Écran neuf mais rétroéclairage en panne." },
  { icon: ScanFace, title: "Face ID / tactile / signal", desc: "Perte après une chute ou un dégât des eaux." },
  { icon: Database, title: "Récupération de données", desc: "Sur un appareil dont la carte mère est endommagée." },
];

const method = [
  { icon: Microscope, title: "Diagnostic binoculaire", sub: "À fort grossissement." },
  { icon: Search, title: "Composant identifié", sub: "IC, condensateur, nappe, filtre." },
  { icon: Wrench, title: "Dessoudage & remplacement", sub: "Au niveau du composant." },
  { icon: CheckCircle2, title: "Test & données", sub: "Priorité à la sauvegarde." },
];

const appareils = [
  { icon: Smartphone, title: "iPhone", desc: (<>Carte mère, circuit de charge, Face ID, rétroéclairage (voir <Link to="/reparation-iphone-montpellier" className="text-blue-600 font-medium hover:underline">réparation iPhone</Link>).</>) },
  { icon: Smartphone, title: "Samsung & Android", desc: (<>Cartes mères Galaxy et autres Android (voir <Link to="/reparation-samsung-montpellier" className="text-blue-600 font-medium hover:underline">réparation Samsung</Link>).</>) },
  { icon: Gamepad2, title: "Consoles", desc: "PS, Switch, Xbox — ports HDMI, alimentation." },
];

const proofItems = [
  { icon: Search, label: 'Diagnostic gratuit' },
  { icon: Microscope, label: 'À la binoculaire' },
  { icon: Cpu, label: 'Carte mère & IC' },
  { icon: Database, label: 'Récupération de données' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const MicroSoudure = () => (
  <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Micro-soudure & carte mère' }]}>
    <Seo
      title="Micro-soudure & Carte Mère Montpellier | AlloRéparations"
      description="Micro-soudure à Montpellier : réparation de carte mère, connecteur de charge, IC, nappes. iPhone, Samsung, console. Devis gratuit."
      canonicalPath={SLUG}
      service={{
        name: "Micro-soudure & réparation de carte mère à Montpellier",
        serviceType: "Micro-soudure et réparation de carte mère",
        description: "Micro-soudure à Montpellier sur iPhone, Samsung et consoles : carte mère, circuit de charge, IC, nappes. Diagnostic gratuit.",
      }}
      faq={faq}
      breadcrumb={[
        { name: 'Accueil', path: '/' },
        { name: 'Micro-soudure & réparation de carte mère à Montpellier', path: SLUG },
      ]}
    />

    <ServiceHeroPro
      eyebrow="Micro-soudure — Montpellier"
      h1Before="Micro-soudure & réparation de "
      h1Highlight="carte mère"
      h1After=" à Montpellier"
      intro="Téléphone qui ne s'allume plus, reste en boucle, ne charge plus malgré un connecteur neuf, ou ne reconnaît plus le Face ID ? Ces pannes se règlent au niveau de la carte mère. AlloRéparations réalise la micro-soudure à Montpellier sur iPhone, Samsung et consoles, avec diagnostic et devis gratuits."
    />

    <ProofStrip items={proofItems} />

    <ServiceSection title="Quand la micro-soudure est-elle nécessaire ?">
      <p>
        Quand la panne ne vient pas d'une pièce remplaçable (écran, batterie) mais d'un composant soudé sur la
        carte mère. C'est une intervention de précision, à la binoculaire, réservée aux cas que les réparations
        standard ne résolvent pas.
      </p>
    </ServiceSection>

    <DiagnosisList items={cas} />

    <ProcessTimeline title="Notre méthode" steps={method} />

    <DiagnosisList title="Appareils pris en charge" items={appareils} />

    <PriceCardPro
      eyebrow="Tarifs"
      title="Tarifs micro-soudure"
      columns={['Réparation', 'Délai', 'Prix TTC']}
      rows={[
        ['Diagnostic en micro-soudure', '–', 'Gratuit'],
        ['Circuit / connecteur de charge', 'sur devis', 'sur devis'],
        ["Carte mère (ne s'allume plus)", 'sur devis', 'sur devis'],
        ['Récupération de données', 'sur devis', 'sur devis'],
      ]}
      note="Diagnostic gratuit — le devis final dépend de l'état de la carte mère et de la panne identifiée."
    />

    <AvisStrip rating={RATING} count={REVIEW_COUNT} reviews={REVIEWS.slice(2, 5)} />

    <ServiceFAQ faq={faq} />

    <LocalCTA heading="Panne de carte mère ? Le diagnostic est gratuit." sub="iPhone, Samsung, console — micro-soudure à la binoculaire, récupération de données." />

    <RelatedServices
      links={[
        { label: 'Réparation iPhone', to: '/reparation-iphone-montpellier', desc: 'Carte mère, charge, Face ID, rétroéclairage.' },
        { label: 'Réparation Samsung Galaxy', to: '/reparation-samsung-montpellier', desc: 'Cartes mères Android et pliables.' },
        { label: 'Tous nos services & consoles', to: '/#services', desc: 'Récupération de données, consoles PS/Switch/Xbox.' },
      ]}
    />
  </ServicePageLayout>
);

export default MicroSoudure;
